import { useLazyQuery } from '@vue/apollo-composable';
import { SCHOOL_COURSE_REGISTRATION } from '@/graphql/Queries/tertiary';
import type { CourseRegistrationFilterInput } from '@/types/composition-functions';

interface Variables {
  academic_calendar_id?: number;
  first: number;
  page: number;
  filter?: CourseRegistrationFilterInput;
}

export const useSchoolCourseRegistration = ({
  autoFetch = true,
  first = 10,
  page = 1,
}: { autoFetch?: boolean; first?: number; page?: number } = {}) => {
  const variables = ref<Variables>({
    first,
    page,
    filter: {},
  });

  const {
    result,
    loading,
    error,
    refetch,
    load,
    onResult,
  } = useLazyQuery(SCHOOL_COURSE_REGISTRATION, variables.value);

  if (autoFetch) load();

  const registrations = computed(
    () => result.value?.schoolCourseRegistration?.data ?? [],
  );

  const paginatorInfo = computed(
    () => result.value?.schoolCourseRegistration?.paginatorInfo ?? null,
  );

  const total = computed(() => paginatorInfo.value?.total ?? 0);

  const setFilter = async(
    filter: CourseRegistrationFilterInput,
    opts: { ignoreAcademicCalendar?: boolean } = {},
  ) => {
    variables.value.filter = { ...filter };
    variables.value.page = 1;
    if (opts.ignoreAcademicCalendar) {
      const saved = variables.value.academic_calendar_id;
      delete variables.value.academic_calendar_id;
      try {
        await refetch({ ...variables.value });
      } finally {
        if (saved != null) variables.value.academic_calendar_id = saved;
      }
    } else {
      await refetch({ ...variables.value });
    }
  };

  const clearFilter = async() => {
    variables.value.filter = {};
    variables.value.page = 1;
    await refetch({ ...variables.value });
  };

  const setAcademicCalendar = async(calendarId: number | null | undefined) => {
    if (calendarId == null) {
      delete variables.value.academic_calendar_id;
    } else {
      variables.value.academic_calendar_id = calendarId;
    }
    variables.value.page = 1;
    await refetch({ ...variables.value });
  };

  /* ---------------- PAGINATION ---------------- */
  const setPage = async(newPage: number) => {
    variables.value.page = newPage;
    await refetch({ ...variables.value });
  };

  const setItemsPerPage = async(newFirst: number) => {
    variables.value.first = newFirst;
    variables.value.page = 1;
    await refetch({ ...variables.value });
  };

  return {
    registrations,
    paginatorInfo,
    total,
    loading,
    error,
    refetch,
    load,
    onResult,
    setPage,
    setItemsPerPage,
    setFilter,
    clearFilter,
    setAcademicCalendar,
  };
};
